import { Root, Track, Range, Thumb } from '@radix-ui/react-slider';
import { BaseSyntheticEvent, useState, type FC } from 'react';
import { validatedDecimalStr } from '@/utils/value-format';
import { useEffect } from 'react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface CustomSliderProps {
  onChange: (val: number) => boolean | void;
  value: number;
  minValue: number;
  maxValue: number;
  unitSymbol: string;
  step?: number;
  buttonValues?: number[];
  disabled?: boolean;
  hideBoxesMobile?: boolean;
  getIsButtonDisabled?: (value: number) => boolean;
  disabledButtonText?: string;
}

export const CustomSlider: FC<CustomSliderProps> = ({
  onChange,
  value,
  minValue,
  maxValue,
  buttonValues,
  step = 0.01,
  unitSymbol = 'x',
  disabled,
  hideBoxesMobile = false,
  getIsButtonDisabled,
  disabledButtonText,
}) => {
  const handleSliderChange = (newValue: number[]) => {
    const isValid = onChange(newValue[0]);
    if (typeof isValid !== 'boolean' || !!isValid) {
      setSliderInput(String(newValue[0]));
    }
  };

  const [sliderInput, setSliderInput] = useState(String(value));
  const handleSliderInputChange = (e: BaseSyntheticEvent) => {
    if (e.target.value === '') {
      setSliderInput('');
      return;
    }

    const value = validatedDecimalStr(e.target.value, -Math.log10(step), 3);

    if (Number(value) < minValue || Number(value) > maxValue) {
      return;
    }

    onChange(Number(value));
  };

  useEffect(() => {
    setSliderInput(String(value));
  }, [value]);

  const handleButtonClick = (buttonValue: number) => {
    handleSliderChange([buttonValue]);
  };

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="grid grid-cols-[1fr_max-content] items-center gap-3">
        <Root
          disabled={disabled}
          className="relative flex h-10 w-full cursor-pointer touch-none select-none items-center disabled:opacity-60"
          value={[value]}
          onValueChange={handleSliderChange}
          min={minValue}
          max={maxValue}
          step={step}
        >
          <Track className="relative mr-1 h-[3px] grow rounded-full bg-border">
            <Range className="absolute h-full rounded-full bg-primary" />
          </Track>
          <Thumb
            className="relative flex h-5 w-5 cursor-pointer items-center justify-center focus:outline-none"
            aria-label="Leverage"
          >
            <svg
              width="18"
              height="20"
              viewBox="0 0 14 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-primary"
            >
              <path
                d="M9.53674e-07 -6.11959e-07L2.54292e-07 16L14 8L9.53674e-07 -6.11959e-07Z"
                fill="currentColor"
              />
            </svg>
            <div className="absolute inset-0 h-full w-full bg-primary/30 blur-md"></div>
          </Thumb>
        </Root>
        <div className="flex h-10 items-center justify-center bg-vestgrey-800 transition-colors hover:bg-border">
          <input
            type="text"
            value={sliderInput}
            onChange={handleSliderInputChange}
            disabled={disabled}
            className="h-full max-w-[5ch] bg-transparent pl-2 text-center font-mono text-base text-foreground focus:outline-none disabled:opacity-60"
          />
          <p className="px-2 font-mono text-base text-foreground">
            {unitSymbol}
          </p>
        </div>
      </div>
      {buttonValues && buttonValues.length > 0 && (
        <div
          // style={{ gridTemplateColumns: `repeat(${buttonValues.length}, 1fr)` }}
          className={cn(
            'h-10 w-full border border-border',
            hideBoxesMobile ? 'hidden md:flex' : 'flex',
          )}
        >
          {buttonValues.map((buttonValue) =>
            getIsButtonDisabled?.(buttonValue) && disabledButtonText ? (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger>
                    <div className="w-full flex-1">
                      <button
                        disabled
                        type="button"
                        className="h-full w-full border-r border-border bg-background font-mono text-foreground transition-colors last:border-r-0 hover:bg-vestgrey-800 disabled:cursor-not-allowed disabled:bg-background disabled:opacity-60"
                      >
                        {buttonValue}
                        {unitSymbol}
                      </button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {disabledButtonText}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <button
                key={buttonValue}
                disabled={disabled || getIsButtonDisabled?.(buttonValue)}
                type="button"
                className="flex-1 border-r border-border bg-background font-mono text-foreground transition-colors last:border-r-0 hover:bg-vestgrey-800 disabled:cursor-not-allowed disabled:bg-background disabled:opacity-60"
                onClick={() => handleButtonClick(buttonValue)}
              >
                {buttonValue}
                {unitSymbol}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
};
